import { Checkbox, Transfer } from 'antd';
import { differenceWith, groupBy, includes, map, some } from 'lodash-es';
import {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import { normalizeString } from '../../common/utils/stringUtils';
import { FAIcon } from '../adapters/fontAwesomeAdapters';
import { Scrollbars } from '../layout/Scrollbars';
import { useScrollbarsStyles } from '../layout/Scrollbars.styles';
import { FormItem, FormItemOverrideContext } from './FormItem';
import { useTransferStyles } from './Transfer.styles';

const ROW_HEIGHT = 40;

const SelectableRow = memo(
  ({ item, selected, onSelected, isEditing, style, alt }) => {
    const { styles } = useTransferStyles();

    return (
      <div
        className={classNames(styles.transferRow, 'ellipsis', {
          [styles.transferRowAlt]: alt,
        })}
        style={style}
      >
        {isEditing ? (
          <Checkbox checked={selected} onChange={() => onSelected(item)}>
            <div title={item.title}>{item.title}</div>
          </Checkbox>
        ) : (
          <span title={item.title}>{item.title}</span>
        )}
      </div>
    );
  }
);

const VirtualRow = ({ index, style, data }) => {
  const item = data.items[index];
  return (
    <SelectableRow
      item={item}
      style={style}
      alt={index % 2 === 1}
      selected={data.selectedSet.has(item.key)}
      onSelected={data.onSelected}
      isEditing={data.isEditing}
    />
  );
};

const VirtualizedScrollbars = forwardRef(
  ({ onScroll, style, children, ...rest }, ref) => (
    <Scrollbars
      {...rest}
      onScroll={onScroll}
      style={{ ...style, overflow: 'hidden' }}
      scrollbarsRef={ref}
    >
      {children}
    </Scrollbars>
  )
);

function TransferSection({
  items,
  listSelectedKeys,
  onSelected,
  isEditing,
  scrollbarsHeight,
  className,
}) {
  const { styles: scrollbarsStyles } = useScrollbarsStyles();
  const { styles } = useTransferStyles(scrollbarsStyles);

  const selectedSet = new Set(listSelectedKeys);

  const onSelectedRef = useRef(onSelected);
  onSelectedRef.current = onSelected;
  const stableOnSelected = useCallback(item => onSelectedRef.current(item), []);

  const rowItems = items || [];
  const itemData = {
    items: rowItems,
    selectedSet,
    onSelected: stableOnSelected,
    isEditing,
  };

  return (
    <div className={className}>
      <div
        className={styles.transferSectionScrollbars}
        style={scrollbarsHeight ? { height: scrollbarsHeight } : undefined}
      >
        <AutoSizer>
          {({ width, height }) => (
            <FixedSizeList
              width={width}
              height={height}
              itemCount={rowItems.length}
              itemSize={ROW_HEIGHT}
              itemData={itemData}
              outerElementType={VirtualizedScrollbars}
            >
              {VirtualRow}
            </FixedSizeList>
          )}
        </AutoSizer>
      </div>
    </div>
  );
}

const TransferInner = ({
  selectedKeys,
  disabled: viewMode,
  titles,
  value,
  onChange,
  getSection,
  transferSections,
  updateSelection,
  getTargetKeys = keys => keys,
  ...props
}) => {
  const { formatMessage } = useIntl();

  const onItemsMove = (_values, direction, movedKeys) => {
    const newValues = updateSelection(value, direction, movedKeys);
    if (newValues) onChange(newValues);
  };

  const targetKeys = getTargetKeys(value);

  const isFiltered = (inputValue, option) =>
    includes(normalizeString(option.title), normalizeString(inputValue));

  return (
    <Transfer
      targetKeys={targetKeys}
      selectedKeys={selectedKeys}
      render={item => item.title}
      showSelectAll={false}
      showSearch={{
        placeholder: formatMessage({
          id: 'admin.accountManagement.search.placeholder-short',
        }),
      }}
      operations={[
        <FAIcon icon="chevron-right" />,
        <FAIcon icon="chevron-left" />,
      ]}
      titles={map(titles, title => formatMessage({ id: title }))}
      onChange={onItemsMove}
      filterOption={isFiltered}
      {...props}
    >
      {({
        filteredItems,
        onItemSelectAll,
        onItemSelect,
        selectedKeys: listSelectedKeys,
      }) => {
        const selectedKeySet = new Set(selectedKeys);

        const onItemSelectFunc = it => {
          onItemSelect(it.key, !selectedKeySet.has(it.key));
        };

        const onAllItemSelectFunc = items => {
          const select = some(
            differenceWith(items, selectedKeys, (i, key) => i.key === key)
          );
          onItemSelectAll(
            map(items, s => s.key),
            select
          );
        };

        const allFilteredSelected =
          filteredItems.length > 0 &&
          filteredItems.every(item => selectedKeySet.has(item.key));

        const groupedItems = groupBy(filteredItems, item => getSection(item));
        return (
          <>
            {!viewMode && (
              <div className="transfer-select-all">
                <Checkbox
                  checked={allFilteredSelected}
                  disabled={filteredItems.length === 0}
                  onChange={() => onAllItemSelectFunc(filteredItems)}
                />
              </div>
            )}
            {map(transferSections, section => (
              <TransferSection
                key={section.key}
                listSelectedKeys={listSelectedKeys}
                onSelected={onItemSelectFunc}
                items={groupedItems[section.key]}
                isEditing={!viewMode}
                scrollbarsHeight={section.scrollbarsHeight}
                className={section.className}
              />
            ))}
          </>
        );
      }}
    </Transfer>
  );
};

const TransferFormItem = ({ name, ...rest }) => {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const { viewMode, disabled } = useContext(FormItemOverrideContext);

  const onSelectChange = (sourceSelectedKeys, targetSelectedKeys) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
  };

  useEffect(() => {
    if (viewMode || disabled) {
      setSelectedKeys([]);
    }
  }, [viewMode, disabled]);

  return (
    <FormItem
      name={name}
      component={TransferInner}
      formItemComponentProps={{
        selectedKeys,
        onSelectChange,
        ...rest,
      }}
    />
  );
};

export default TransferFormItem;
